import React, { useEffect, useState } from 'react';
import { Grid, Box, FormControl, InputLabel, MenuItem, Select } from '@mui/material';
import dayjs from 'dayjs';

import {
  MonetizationOn as MonetizationOnIcon,
  Percent as PercentIcon,
  Receipt as ReceiptIcon,
  BarChart as BarChartIcon,
  Business as BusinessIcon,
  Storefront as StorefrontIcon,
} from '@mui/icons-material';

import { getSalesDashboardData } from './../../../services/ReportsSalesService';

import PageHeader from './../../../components/PageHeader/PageHeader';
import SummaryCards from '../../../components/Reports/SummaryCards';
import TopProductsChart from '../../../components/Reports/Sales/TopProductsChart';
import SalesTrendChartStacked from '../../../components/Reports/Sales/SalesTrendChartStacked';
import BranchSalesChart from '../../../components/Reports/Sales/BranchSalesChart';
import MonthYearSelector from '../../../components/Reports/Sales/MonthYearSelector';
import SalesByHourChart from './../../../components/Reports/Sales/SalesByHourChart';
import SalesComparisonChart from './../../../components/Reports/Sales/SalesComparisonChart';
import BranchSalesTrendChart from '../../../components/Reports/Sales/BranchSalesTrendChart';
import { useTranslation } from 'react-i18next';
import PaymentMethodsChart from '../../../components/Reports/Sales/PaymentMethodsChart';
import CategorySalesChart from '../../../components/Reports/Sales/CategorySalesChart';
import AverageSalesByBranchChart from '../../../components/Reports/Sales/AverageSalesByBranchChart';
import SellerPerformanceTable from '../../../components/Reports/Sales/SellerPerformanceTable';
import SalesByChannelChart from '../../../components/Reports/Sales/SalesByChannelChart';
import B2BDailyTrendChart from '../../../components/Reports/Sales/B2BDailyTrendChart';
import { formatMoney } from '../../../utils/currency';

import useReportCurrency from '../../../hooks/useReportCurrency';
import ReportCurrencyPicker from '../../../components/Currency/ReportCurrencyPicker';
const SalesReports = () => {
  const [reportCurrency, setReportCurrency] = useReportCurrency();
  const { t } = useTranslation();

  const storedRole = localStorage.getItem('role') || '';
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [month, setMonth] = useState(dayjs().month() + 1);
  const [year, setYear] = useState(dayjs().year());
  const [reportView, setReportView] = useState('FINAL');

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      const result = await getSalesDashboardData({ month, year });
      setData(result);
      setLoading(false);
    };
  
    fetch();
  }, [month, year, reportCurrency]);  

  const b2bBreakdown = data?.b2bBreakdown || {};
  const b2bExternal = b2bBreakdown.external || {};
  const b2bFranchise = b2bBreakdown.franchise || {};
  const b2bTotal = b2bBreakdown.total || {};
  const ownedPlusB2B = data?.ownedPlusB2B || null;
  const finalSummary = data?.summary || {};
  const isFranchiseRole = ['FRANCHISE_MANAGER', 'Franquiciante', 'Franquiciado'].includes(storedRole);
  const hasB2BBreakdown = Number(b2bTotal.totalAmount || 0) > 0;
  const hasOwnedPlusB2B = !!ownedPlusB2B;
  const showReportViewSelector = !isFranchiseRole && (hasB2BBreakdown || hasOwnedPlusB2B);
  const isOwnedPlusB2BView = reportView === 'OWNED_PLUS_B2B';
  const isB2BExternalView = reportView === 'B2B_EXTERNAL';
  const isB2BFranchiseView = reportView === 'B2B_FRANCHISE';
  const isB2BOnlyView = isB2BExternalView || isB2BFranchiseView;
  const activeSalesData = isOwnedPlusB2BView && ownedPlusB2B ? ownedPlusB2B : data;
  const selectedB2BSummary = isB2BExternalView ? b2bExternal : b2bFranchise;
  const selectedB2BTrend = isB2BExternalView
    ? b2bBreakdown.externalTrend || []
    : b2bBreakdown.franchiseTrend || [];
  const selectedB2BTitle = isB2BExternalView ? 'B2B externo' : 'Abastecimiento franquicias';

  const summaryItems = (() => {
    if (!data) return [];

    if (isB2BOnlyView) {
      return [
        {
          title: isB2BExternalView ? 'B2B externo' : 'Abastecimiento franquicias',
          icon: isB2BExternalView
            ? <BusinessIcon color="secondary" fontSize="large" />
            : <StorefrontIcon color="warning" fontSize="large" />,
          value: formatMoney(selectedB2BSummary.totalAmount),
        },
        {
          title: 'Tickets',
          icon: <ReceiptIcon color="success" fontSize="large" />,
          value: Number(selectedB2BSummary.ticketCount || 0),
        },
        {
          title: 'Ticket promedio',
          icon: <BarChartIcon color="action" fontSize="large" />,
          value: formatMoney(selectedB2BSummary.averageTicket),
        },
        {
          title: 'Participación B2B',
          icon: <PercentIcon color="secondary" fontSize="large" />,
          value: `${Number(b2bTotal.totalAmount || 0) > 0
            ? ((Number(selectedB2BSummary.totalAmount || 0) / Number(b2bTotal.totalAmount || 1)) * 100).toFixed(1)
            : '0.0'} %`,
        },
      ];
    }

    const standardSummary = activeSalesData?.summary || finalSummary;
    return [
      {
        title: isOwnedPlusB2BView ? 'Locales propios + B2B' : 'Venta final',
        icon: <MonetizationOnIcon color="primary" fontSize="large" />,
        value: formatMoney(standardSummary.totalAmount),
      },
      {
        title: 'Ventas con descuento',
        icon: <PercentIcon color="secondary" fontSize="large" />,
        value: `${Number(standardSummary.discountPercent || 0).toFixed(1)} %`,
      },
      {
        title: 'Cantidad de tickets',
        icon: <ReceiptIcon color="success" fontSize="large" />,
        value: Number(standardSummary.ticketCount || 0),
      },
      {
        title: 'Ticket promedio',
        icon: <BarChartIcon color="action" fontSize="large" />,
        value: formatMoney(standardSummary.averageTicket),
      },
    ];
  })();

  const b2bChannelData = [
    {
      channel: 'B2B_EXTERNAL',
      value: Number(b2bExternal.totalAmount || 0),
      ticketCount: Number(b2bExternal.ticketCount || 0),
    },
    {
      channel: 'B2B_FRANCHISE',
      value: Number(b2bFranchise.totalAmount || 0),
      ticketCount: Number(b2bFranchise.ticketCount || 0),
    },
  ].filter((item) => item.value > 0 || item.ticketCount > 0);
  const selectedB2BChannelData = b2bChannelData.filter((item) => item.channel === reportView);

  useEffect(() => {
    if (
      (!showReportViewSelector && reportView !== 'FINAL') ||
      (reportView === 'OWNED_PLUS_B2B' && !hasOwnedPlusB2B) ||
      ((reportView === 'B2B_EXTERNAL' || reportView === 'B2B_FRANCHISE') && !hasB2BBreakdown)
    ) {
      setReportView('FINAL');
    }
  }, [showReportViewSelector, reportView, hasOwnedPlusB2B, hasB2BBreakdown]);

  return (
    <Box p={3}>
      {/* Header y rango de fechas */}
      <Grid container spacing={2} alignItems="center" justifyContent="space-between">
        <Grid item>
          <PageHeader title={t('sales')} subtitle={t('Seguimiento de ventas, ingresos y tickets')} />
      <ReportCurrencyPicker currency={reportCurrency} onChange={setReportCurrency} />
        </Grid>
        <Grid item xs={12} md="auto">
          <Box
            display="flex"
            alignItems="center"
            gap={2}
            justifyContent="flex-end"
            sx={{ flexWrap: { xs: 'wrap', sm: 'nowrap' } }}
          >
            {showReportViewSelector && (
              <FormControl size="small" sx={{ width: { xs: '100%', sm: 260 } }}>
                <InputLabel id="sales-report-view-label">Vista</InputLabel>
                <Select
                  labelId="sales-report-view-label"
                  value={reportView}
                  label="Vista"
                  onChange={(event) => setReportView(event.target.value)}
                >
                  <MenuItem value="FINAL">Venta final</MenuItem>
                  {hasOwnedPlusB2B && (
                    <MenuItem value="OWNED_PLUS_B2B">Locales propios + B2B</MenuItem>
                  )}
                  {hasB2BBreakdown && (
                    <MenuItem value="B2B_EXTERNAL">B2B externo</MenuItem>
                  )}
                  {hasB2BBreakdown && (
                    <MenuItem value="B2B_FRANCHISE">Abastecimiento franquicias</MenuItem>
                  )}
                </Select>
              </FormControl>
            )}
            <MonthYearSelector
              month={month}
              setMonth={setMonth}
              year={year}
              setYear={setYear}
              sx={{ width: { xs: '100%', sm: 300 } }}
            />
          </Box>
        </Grid>
      </Grid>

      <Box mt={2}>
        <SummaryCards items={summaryItems} loading={loading} />
      </Box>

      {isB2BOnlyView ? (
        <Grid container spacing={2} mt={0} mb={5}>
          <Grid item xs={12} md={8}>
            <B2BDailyTrendChart
              data={selectedB2BTrend}
              loading={loading}
              title={`${selectedB2BTitle} por dia`}
              color={isB2BExternalView ? '#7e57c2' : '#ff7043'}
              hasSummaryData={Number(selectedB2BSummary.totalAmount || 0) > 0}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SalesByChannelChart
              data={selectedB2BChannelData}
              loading={loading}
              title="Resumen"
            />
          </Grid>
        </Grid>
      ) : (
        <>
          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={8}>
              <SalesTrendChartStacked
                data={activeSalesData?.branchSalesTrend}
                branches={activeSalesData?.branches}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <BranchSalesChart
                data={activeSalesData?.branchSales}
                branches={activeSalesData?.branches}
                loading={loading}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={4}>
            <Grid item xs={12}>
              <BranchSalesTrendChart
                data={activeSalesData?.branchSalesTrend}
                branches={activeSalesData?.branches}
                loading={loading}
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={0}>
            <Grid item xs={12} md={6}>
              <TopProductsChart data={activeSalesData?.topProducts || []} loading={loading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SalesByChannelChart data={activeSalesData?.salesByChannel || []} loading={loading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <PaymentMethodsChart data={activeSalesData?.paymentMethods || []} loading={loading} />
            </Grid>
            <Grid item xs={12} md={6}>
              <SalesByHourChart data={activeSalesData?.salesByHour || []} loading={loading} />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={4}>
            <Grid item xs={12} md={4}>
              <CategorySalesChart data={activeSalesData?.salesByCategory || []} loading={loading} />
            </Grid>
            <Grid item xs={8}>
              <SalesComparisonChart data={activeSalesData?.salesComparison} loading={loading} />
            </Grid>
          </Grid>

          <Grid container spacing={2} mt={4}>
            <Grid item xs={12} md={8}>
              <SellerPerformanceTable
                data={activeSalesData?.performanceBySeller}
                loading={loading}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <AverageSalesByBranchChart 
                data={activeSalesData?.averageSalesByBranch || []} 
                branches={activeSalesData?.branches}
                loading={loading} 
              />
            </Grid>
          </Grid>

        </>
      )}
    </Box>
  );
};

export default SalesReports;
